import PropTypes from 'prop-types';
import { isEmpty, uniqueId } from 'lodash';
import Icon from "../../Icon/Icon";
import AllOrOne from "../../AllOrOne/AllOrOne";
import EmailAddress from "../../Contact/EmailAddress/EmailAddress";
import HoursOfOperation from "../../HoursOfOperation/HoursOfOperation";
import addresses from "../../../utils/Addresses";
import phones from "../../../utils/Phones";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function Location(props) {
  const {
    addressCount = null,
    className = '',
    clickableAddress = false,
    disableEmailLink = false,
    disablePhoneLink = false,
    hideLabels = false,
    isInNetworkGroup = true,
    location: locationProp,
    originLatLng = [],
    showHours = true,
    showHoursIcon = false,
    showMore = false,
    stopEventPropagation = false,
    additionalContentComponent = null,
    intlProvider = {}
  } = props;
  const location = addresses.calculateDistance(locationProp, originLatLng);
  const locationAddress = loc => !isEmpty(originLatLng) ? `${loc.displayName} (${loc.distance})` : `${loc.displayName}`;
  const locationAddressWithLink = () => clickableAddress ? /*#__PURE__*/_jsx("a", {
    href: addresses.makeGoogleMapLink(location),
    rel: "noopener noreferrer",
    target: "_blank",
    className: "locations-address-link",
    children: locationAddress(location)
  }) : locationAddress(location);
  const formattedPhoneNumber = phoneNumber => !hideLabels && phoneNumber.phone_type ? `${phoneNumber.phone_type}: ${phones.formatPhoneNumber(phoneNumber.phone_number)}` : phones.formatPhoneNumber(phoneNumber.phone_number);
  const AdditionalContentComponent = additionalContentComponent;
  return (
    /*#__PURE__*/
    // this element has interactable children and the handlers allow for optionally
    // stopping interactions with Locations from bubbling up to any container elements
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    _jsxs("div", {
      className: `location ${className}`,
      onClick: e => {
        if (stopEventPropagation) {
          e.stopPropagation();
        }
      },
      onKeyDown: e => {
        if (stopEventPropagation && e.key === 'Enter') {
          e.stopPropagation();
        }
      },
      children: [/*#__PURE__*/_jsxs("div", {
        className: "location-address items-start flex",
        children: [/*#__PURE__*/_jsx(Icon, {
          className: "location-icon flex-1.5",
          color: `${isInNetworkGroup ? 'action-blue' : 'brand-blue'}`,
          icon: "MapPin",
          size: 18
        }), /*#__PURE__*/_jsxs("div", {
          className: "location-content leading-6 pl-1 text-sm text-text-blue",
          children: [!hideLabels && location.name ? location.name : /*#__PURE__*/_jsxs("p", {
            className: "location-address-name flex flex-wrap capitalize",
            children: [/*#__PURE__*/_jsx("span", {
              className: "mr-2",
              children: locationAddressWithLink(location)
            }), /*#__PURE__*/_jsx("span", {
              className: "location-address-name-more text-action-blue",
              children: !isEmpty(addressCount) ? addressCount : ''
            })]
          }), !hideLabels && location.name && /*#__PURE__*/_jsxs("p", {
            className: "location-address items-start flex",
            children: [/*#__PURE__*/_jsx("span", {
              className: "mr-2",
              children: locationAddressWithLink(location)
            }), /*#__PURE__*/_jsx("span", {
              className: "location-address-name-more text-action-blue",
              children: !isEmpty(addressCount) ? addressCount : ''
            })]
          }), /*#__PURE__*/_jsx(AllOrOne, {
            showAll: showMore,
            children: !isEmpty(location.phone_numbers) && location.phone_numbers.map(phoneNumber => /*#__PURE__*/_jsx("p", {
              className: "location-phone capitalize",
              children: disablePhoneLink ? formattedPhoneNumber(phoneNumber) : /*#__PURE__*/_jsx("a", {
                href: phones.getTelLink(phoneNumber),
                target: "_blank",
                tabIndex: 0,
                rel: "noopener noreferrer",
                className: "text-action-blue",
                children: formattedPhoneNumber(phoneNumber)
              })
            }, `phone-${phoneNumber.phone_number}`))
          }), /*#__PURE__*/_jsx(AllOrOne, {
            showAll: showMore,
            children: !isEmpty(location.email_addresses) && location.email_addresses.map(email => /*#__PURE__*/_jsx("p", {
              className: "location-email",
              children: /*#__PURE__*/_jsx(EmailAddress, {
                className: "program-details-email-text",
                disableLink: disableEmailLink,
                emailAddress: email
              })
            }, `email-${email}`))
          })]
        })]
      }, uniqueId(location.name || location.displayName)), showHours && /*#__PURE__*/_jsx("div", {
        className: "location-hours pl-1",
        children: /*#__PURE__*/_jsx(HoursOfOperation, {
          hours: location.hours || location.hours_of_operation,
          isInNetworkGroup: isInNetworkGroup,
          showAll: showMore,
          showHoursIcon: showHoursIcon,
          showToggle: true,
          intlProvider: intlProvider
        })
      }), additionalContentComponent && /*#__PURE__*/_jsx(AdditionalContentComponent, {
        location: location
      })]
    })
  );
}
Location.propTypes = {
  addressCount: PropTypes.string,
  className: PropTypes.string,
  clickableAddress: PropTypes.bool,
  disableEmailLink: PropTypes.bool,
  disablePhoneLink: PropTypes.bool,
  hideLabels: PropTypes.bool,
  isInNetworkGroup: PropTypes.bool,
  location: PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
    attributes: PropTypes.shape({
      line_1: PropTypes.string,
      line_2: PropTypes.string,
      city: PropTypes.string,
      county: PropTypes.string,
      state: PropTypes.string,
      postal_code: PropTypes.string,
      country: PropTypes.string,
      latitude: PropTypes.number,
      longitude: PropTypes.number,
      name: PropTypes.string,
      phone_numbers: PropTypes.arrayOf(PropTypes.shape({
        phone_type: PropTypes.string,
        country_code: PropTypes.string,
        phone_number: PropTypes.string
      })),
      email_addresses: PropTypes.arrayOf(PropTypes.string)
    }),
    relationships: PropTypes.shape({
      provider: PropTypes.shape({
        data: PropTypes.shape({
          id: PropTypes.string,
          type: PropTypes.string
        })
      }),
      programs: PropTypes.shape({
        data: PropTypes.arrayOf(PropTypes.shape({
          id: PropTypes.string,
          type: PropTypes.string
        }))
      })
    })
  }).isRequired,
  originLatLng: PropTypes.arrayOf(PropTypes.number),
  showHours: PropTypes.bool,
  showHoursIcon: PropTypes.bool,
  showMore: PropTypes.bool,
  stopEventPropagation: PropTypes.bool,
  /**
   * Property to pass a component that will render in the main content area of the component.
   * This feature is used to render an edit location link.
   */
  additionalContentComponent: PropTypes.func,
  // Detailed intlProvider shape: https://formatjs.github.io/docs/react-intl/components/#intlprovider
  intlProvider: PropTypes.object // eslint-disable-line react/forbid-prop-types
};
export default Location;
//# sourceMappingURL=Location.js.map